import { getIdToken } from "../../auth/cognito.js";

async function request(path, options = {}) {
  const token = await getIdToken();
  if (!token) throw new Error("Your session has expired. Sign in again to use GEOAI.");
  const response = await fetch(`/api/geoai${path}`, {
    ...options,
    headers: { Authorization: `Bearer ${token}`, ...options.headers },
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.error || payload.message || `GEOAI request failed (${response.status}).`);
  }
  return response;
}

export async function getStatus(signal) {
  const response = await request("/status", { signal, cache: "no-store" });
  return response.json();
}

export async function deleteSession(id) {
  if (!id) return;
  await request(`/sessions/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// Decode lines rather than splitting entire frames: CR, LF, and CRLF are all
// valid SSE separators, including when a CRLF or UTF-8 character spans chunks.
export function createEventStreamParser(onEvent) {
  const decoder = new TextDecoder();
  let buffer = "";
  let event = "message";
  let data = [];
  function line(value) {
    if (!value) {
      if (data.length) {
        const raw = data.join("\n");
        let payload;
        try { payload = JSON.parse(raw); }
        catch { throw new Error("GEOAI returned an unreadable response. Try again."); }
        onEvent(event, payload);
      }
      event = "message";
      data = [];
      return;
    }
    if (value.startsWith(":")) return;
    const colon = value.indexOf(":");
    const field = colon < 0 ? value : value.slice(0, colon);
    let content = colon < 0 ? "" : value.slice(colon + 1);
    if (content.startsWith(" ")) content = content.slice(1);
    if (field === "event") event = content || "message";
    if (field === "data") data.push(content);
  }
  function process(final = false) {
    let start = 0;
    for (let i = 0; i < buffer.length; i++) {
      const char = buffer[i];
      if (char !== "\n" && char !== "\r") continue;
      if (char === "\r" && i + 1 === buffer.length && !final) break;
      line(buffer.slice(start, i));
      if (char === "\r" && buffer[i + 1] === "\n") i++;
      start = i + 1;
    }
    buffer = buffer.slice(start);
    // Limit an unterminated line so a broken stream cannot consume unbounded RAM.
    if (buffer.length > 16 * 1024 * 1024) throw new Error("GEOAI response exceeded the stream limit.");
  }
  return {
    push(chunk) { buffer += decoder.decode(chunk, { stream: true }); process(); },
    finish() {
      buffer += decoder.decode();
      process(true);
      // Only blank-line-terminated frames are dispatched, as specified by SSE.
    },
  };
}

export async function chat(body, { signal, onEvent }) {
  const response = await request("/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify(body),
    signal,
  });
  if (!response.body || !response.headers.get("content-type")?.includes("text/event-stream")) {
    throw new Error("GEOAI could not start a conversation. Try again.");
  }
  const reader = response.body.getReader();
  const parser = createEventStreamParser(onEvent);
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      parser.push(value);
    }
    parser.finish();
  } finally {
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}
